import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";

import type { ProjectConnectionGroupUpdateRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { createGroupFormPermissions, toGroupPermissionsRequest } from "@/helpers/system-groups/permissions";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { usePermissionQueries } from "@/queries/permissions/queryOptions";
import { projectConnectionGroupMutations } from "@/queries/project-connection-groups/mutations";
import { useProjectConnectionGroupQueries } from "@/queries/project-connection-groups/queryOptions";
import { useProjectConnectionQueries } from "@/queries/project-connections/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import {
  type CreateOrEditProjectConnectionGroupFormValues,
  ProjectConnectionGroupForm,
} from "./ProjectConnectionGroupForm";

export function EditProjectConnectionGroupPage(): React.ReactNode {
  const { id: connectionId, gid: groupId } = useParams<{ id: string; gid: string }>();
  const navigate = useNavigate();
  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const { uploadFormImage, isUploadingImage } = useUploadImage();
  const connectionQueries = useProjectConnectionQueries();
  const groupQueries = useProjectConnectionGroupQueries();
  const permissionQueries = usePermissionQueries();
  const connectionQuery = useQuery(connectionQueries.details(connectionId!));
  const groupQuery = useQuery(groupQueries.details(groupId!, connectionId!));
  const defaultPermissionsQuery = useQuery(permissionQueries.listDefaultTypes());
  const rolesQuery = useQuery(permissionQueries.listRoles());
  const editGroup = projectConnectionGroupMutations.useUpdateProjectConnectionGroup(connectionId ?? "");
  const deleteGroup = projectConnectionGroupMutations.useDeleteProjectConnectionGroup(connectionId ?? "");

  const error =
    !connectionId ||
    !groupId ||
    connectionQuery.error ||
    groupQuery.error ||
    defaultPermissionsQuery.error ||
    rolesQuery.error;
  if (error) {
    return <ErrorPage error={error} />;
  }
  if (
    connectionQuery.isPending ||
    groupQuery.isFetching ||
    defaultPermissionsQuery.isFetching ||
    rolesQuery.isFetching
  ) {
    return <FullSizeLoader withPadding />;
  }
  const group = groupQuery.data;

  const handleSubmit = async (values: CreateOrEditProjectConnectionGroupFormValues) => {
    if (!values.image?.length) {
      showFlashToast({ variant: "alert", message: t("page.project-connection-groups.create.error") });

      return;
    }
    const image = await uploadFormImage(values.image[0]);
    if (!image) {
      return;
    }
    const payload: ProjectConnectionGroupUpdateRequest = {
      type: values.type,
      name: values.name,
      description: values.description,
      imageId: image.id,
      defaultPostingAudience: values.audience,
      permissions: toGroupPermissionsRequest(values.permissions),
      icon: values.icon!,
    };
    await editGroup.mutateAsync({ groupId, payload });
    void navigate(routes.projectConnections.list());
  };
  const handleDelete = () =>
    deleteGroup.mutateAsync(groupId, {
      onSuccess: () => {
        void navigate(routes.projectConnections.list());
      },
    });

  return (
    <ProjectConnectionGroupForm
      connectionId={connectionId}
      groupId={groupId}
      isEditMode
      projectConnection={connectionQuery.data}
      languageId={sessionUser.uiLanguage.id}
      defaultValues={{
        type: group?.type || "interest",
        name: group?.name || "",
        description: group?.description || "",
        icon: group?.icon,
        image: group?.image ? [group.image] : [],
        postingLevel: group?.postingLevel || "projectAndProjectConnection",
        audience: group?.defaultPostingAudience || "projectConnection",
        permissions: createGroupFormPermissions(
          rolesQuery.data ?? [],
          defaultPermissionsQuery.data ?? [],
          group?.permissions,
        ),
      }}
      onSubmit={handleSubmit}
      onDelete={handleDelete}
      isSubmitting={isUploadingImage || editGroup.isPending || deleteGroup.isPending}
    />
  );
}
